import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { TabView, TabPanel } from 'primereact/tabview';
import './ApproachDetailTabsAtom.scss';

export type ApproachDetailTab = 'overview' | 'whatsIncluded' | 'community';

export interface ApproachDetailTabsAtomProps {
  activeTab: ApproachDetailTab;
  onTabChange: (tab: ApproachDetailTab) => void;
  showWhatsIncludedTab: boolean;
  /** Optional count badge rendered next to the Community tab label (e.g. review count). */
  communityCount?: number;
  className?: string;
}

const TAB_ORDER: ApproachDetailTab[] = [
  'overview',
  'whatsIncluded',
  'community',
];

// Leading icon per tab. Matches the channel-detail design's tab affordances:
// book = Overview, layered cards = What's included, chat = Community.
const TAB_ICON: Record<ApproachDetailTab, string> = {
  overview: 'pi pi-book',
  whatsIncluded: 'pi pi-clone',
  community: 'pi pi-comments',
};

const ApproachDetailTabsAtom: React.FC<ApproachDetailTabsAtomProps> = ({
  activeTab,
  onTabChange,
  showWhatsIncludedTab,
  communityCount,
  className = '',
}) => {
  const { t } = useTranslation();

  const visibleTabs = useMemo(
    () =>
      TAB_ORDER.filter(
        (tab) => tab !== 'whatsIncluded' || showWhatsIncludedTab,
      ),
    [showWhatsIncludedTab]
  );

  const activeIndex = useMemo(
    () => Math.max(0, visibleTabs.indexOf(activeTab)),
    [visibleTabs, activeTab]
  );

  const handleTabChange = (index: number) => {
    const tab = visibleTabs[index];
    if (tab) {
      onTabChange(tab);
    }
  };

  const classes = ['marketplace-approach-detail-tabs', className]
    .filter(Boolean)
    .join(' ');

  const renderHeader = (tab: ApproachDetailTab): React.ReactNode => {
    const label = t(`marketplace.approachDetail.tabs.${tab}`);
    const showCount =
      tab === 'community' &&
      typeof communityCount === 'number' &&
      communityCount > 0;
    return (
      <span
        className="marketplace-approach-detail-tabs__header"
        data-tab={tab}
      >
        <i
          className={`${TAB_ICON[tab]} marketplace-approach-detail-tabs__header-icon`}
          aria-hidden="true"
        />
        <span className="marketplace-approach-detail-tabs__header-label">
          {label}
        </span>
        {showCount && (
          <span className="marketplace-approach-detail-tabs__header-count">
            {communityCount}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className={classes}>
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => handleTabChange(e.index)}
      >
        {visibleTabs.map((tab) => (
          <TabPanel key={tab} header={renderHeader(tab)} />
        ))}
      </TabView>
    </div>
  );
};

export default ApproachDetailTabsAtom;
